import { useCallback, useMemo } from 'react';

import { Checkmark, ChevronSmallRight } from '@remarkable/ark-icons';
import { Tag, Typography } from '@remarkable/ark-web';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import type { ClientInformation } from 'src/api/endpoints/cloudApi.types';
import { ClientSource } from 'src/api/endpoints/cloudApi.types';
import { useDeleteDevice, useDevices } from 'src/api/queries';
import { getDeviceName } from 'src/apps/fleet/app/utils/getDeviceName';
import {
  Button,
  Divider,
  Modal,
  NotificationBox,
  Spinner,
  Table,
} from 'src/components';
import { DeviceIcon } from 'src/components/DeviceIcon';
import { formatDate, formatShortDate } from 'src/utils/formatDate';

import { useAuth0 } from '@auth0/auth0-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { DeviceName } from 'src/components/DeviceName';
import { TestId } from 'src/testids';
import { deviceHelpers } from 'src/utils/deviceHelpers';
import { userHelpers } from 'src/utils/userHelpers';
import {
  filterActiveAppClients,
  filterActiveBrowserExtensionClients,
  filterActivePaperTabletClients,
  getClientTitle,
  trackProductConfigured,
} from '../utils/utils';
import { VerifyUnpairModal } from './VerifyUnpairModal';

const columnHelper = createColumnHelper<ClientInformation>();

type DeviceType = 'paper-tablet' | 'apps' | 'read-on-remarkable';

const filterDevices = (
  deviceType: DeviceType,
  connectedDevices?: ClientInformation[]
) => {
  if (!connectedDevices) return [];
  switch (deviceType) {
    case 'paper-tablet':
      return filterActivePaperTabletClients(connectedDevices);
    case 'apps':
      return filterActiveAppClients(connectedDevices);
    case 'read-on-remarkable':
      return filterActiveBrowserExtensionClients(connectedDevices);
    default:
      return [];
  }
};

const DevicePaperTabletDividerText = ({
  connectedDevices,
}: {
  connectedDevices: ClientInformation[];
}) => {
  const activeDevices = useMemo(
    () => filterActivePaperTabletClients(connectedDevices),
    [connectedDevices]
  );
  const auth = useAuth0();
  const maxDeviceCount = deviceHelpers.getMaxDeviceCount(auth.user);

  return (
    <>
      <Trans>Your devices</Trans>
      <Tag
        size="large"
        variant="success-light"
        data-cy="device-count-tag"
        className="ml-16 inline-flex"
      >
        <Checkmark />
        {`${activeDevices.length} / ${maxDeviceCount}`}
      </Tag>
    </>
  );
};

export const DeviceTable = ({ deviceType }: { deviceType: DeviceType }) => {
  const { t } = useLingui();
  const connectedDevices = useDevices();
  const deleteDeviceMutation = useDeleteDevice();
  const auth = useAuth0();

  const handleUnpair = useCallback(
    (device: ClientInformation) => {
      deleteDeviceMutation.mutate(device, {
        onSuccess: () => {
          trackProductConfigured(device, 'device_unpaired');
        },
      });
    },
    [deleteDeviceMutation]
  );

  const getName = (clientInfo: ClientInformation) => {
    return getDeviceName({
      serialNumber: clientInfo.clientId,
      type: clientInfo.clientName,
    });
  };

  const isDeviceManaged = (clientInfo: ClientInformation) => {
    if (!auth.user || !userHelpers.isOrganizationMember(auth.user)) {
      return false;
    }

    return clientInfo.clientSource === ClientSource.ProfileV1;
  };

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('clientName', {
        id: 'clientName',
        header:
          deviceType === 'paper-tablet'
            ? t`Paper tablet`
            : deviceType === 'read-on-remarkable'
              ? t`Extension`
              : t`Application`,
        meta: {
          colWidth: 'w-1/3',
        },
        cell: (info) => (
          <span className="flex flex-row items-center gap-8">
            <DeviceIcon
              size={24}
              className="shrink-0"
              device={info.row.original}
            />
            <Typography
              variant="interface-md"
              fontWeight="medium"
              leadingNone
              className="truncate"
            >
              <DeviceName device={info.row.original} />
            </Typography>
          </span>
        ),
      }),

      columnHelper.accessor((client) => formatDate(client.registeredAt), {
        id: 'registeredAt',
        header: t`Registered`,
        meta: {
          colWidth: 'w-1/5',
        },
      }),

      columnHelper.accessor((client) => client.clientId, {
        id: 'serialNumber',
        header: t`Serial number`,
      }),

      columnHelper.display({
        id: 'unpair',
        header: ' ',
        meta: {
          align: 'right',
          colWidth: 'w-[120px]',
          truncate: false,
        },
        cell: (info) => {
          if (!auth.user) return null;

          if (isDeviceManaged(info.row.original)) {
            return null; // Do not allow unpairing of devices managed by the organization
          }
          return (
            <Modal.Root>
              <VerifyUnpairModal
                onUnpair={() => handleUnpair(info.row.original)}
                title={
                  <Trans>
                    Unpair <DeviceName device={info.row.original} />
                  </Trans>
                }
                isUnpairing={deleteDeviceMutation.isPending}
              />
              <Modal.Trigger asChild>
                <Button
                  data-cy="unpair-button"
                  size="small"
                  variant="tertiary-neutral"
                  aria-label={`Unpair ${getName(
                    info.row.original
                  )} - Registered on ${formatDate(
                    info.row.original.registeredAt
                  )}`}
                >
                  <span className="whitespace-nowrap">
                    <Trans>Unpair</Trans>
                  </span>{' '}
                  <ChevronSmallRight />
                </Button>
              </Modal.Trigger>
            </Modal.Root>
          );
        },
      }),
    ];
  }, [deviceType]);

  const filteredDevices = useMemo(() => {
    return filterDevices(deviceType, connectedDevices.data) || [];
  }, [deviceType, connectedDevices.data]);

  const table = useReactTable({
    data: filteredDevices,
    columns: columns,
    meta: {
      getRowLabel: (row) => getClientTitle(row),
    },
    initialState: {
      columnVisibility: {
        serialNumber: deviceType === 'paper-tablet',
        unpair: !!auth.user,
      },
    },
    getRowId: (clientInformation) => clientInformation.clientId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    renderFallbackValue: () => '-',
  });

  const hasManagedDevices = useMemo(() => {
    if (!auth.user) return false;

    if (!userHelpers.isOrganizationMember(auth.user)) return false;

    return filteredDevices.some(
      (device) => device.clientSource === ClientSource.ProfileV1
    );
  }, [auth.user, filteredDevices]);

  if (connectedDevices.isPending) return <Spinner />;

  if (connectedDevices.isError)
    return <NotificationBox className="mt-24" error={connectedDevices.error} />;

  if (filterDevices(deviceType, connectedDevices.data).length === 0) {
    return null;
  }

  return (
    <>
      <Divider
        title={
          deviceType === 'paper-tablet' ? (
            <DevicePaperTabletDividerText
              connectedDevices={connectedDevices.data}
            />
          ) : deviceType === 'read-on-remarkable' ? (
            t`Your extensions`
          ) : (
            t`Your apps`
          )
        }
        className="my-32"
      />
      {hasManagedDevices && (
        <NotificationBox
          variant="info"
          title={t`Enterprise managed devices`}
          data-cy={TestId.NotificationManagedDevicesNoUnpair}
        >
          <Trans>
            Some of your devices are managed by your organization. To unlink
            these devices from your enterprise account, do a factory reset on
            the device or contact your IT administrator to perform a remote
            wipe.
          </Trans>
        </NotificationBox>
      )}

      <Table table={table} data-cy="device-table" className="hidden lm:table" />
      <ul className="border-t border-gray-100 lm:hidden" data-cy="device-list">
        {table.getRowModel().rows.map((row) => {
          return (
            <li
              data-cy={`device-row-${row.id}`}
              key={row.id}
              className="border-b border-gray-100 py-12"
            >
              <div className="flex w-full flex-row items-center justify-between">
                <div>
                  <Typography variant="body-sm" className="truncate">
                    <span className="font-semibold">
                      <DeviceName device={row.original} />
                    </span>
                    <span className="mx-4">|</span>
                    {formatShortDate(row.original.registeredAt, {
                      showYear: true,
                    })}
                  </Typography>
                  {deviceType === 'paper-tablet' && (
                    <Typography variant="body-sm">
                      {row.original.clientId}
                    </Typography>
                  )}
                </div>

                {isDeviceManaged(row.original) === false && (
                  <Modal.Root>
                    <VerifyUnpairModal
                      onUnpair={() => handleUnpair(row.original)}
                      title={
                        <Trans>
                          Unpair <DeviceName device={row.original} />
                        </Trans>
                      }
                      isUnpairing={deleteDeviceMutation.isPending}
                    />
                    <Modal.Trigger asChild>
                      <Button variant="tertiary-neutral" size="medium">
                        <span className="whitespace-nowrap">
                          <Trans>Unpair</Trans>
                        </span>{' '}
                        <ChevronSmallRight />
                      </Button>
                    </Modal.Trigger>
                  </Modal.Root>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
};
